import React, { useMemo, Suspense } from 'react'
import { useTexture } from '@react-three/drei'
import { useDesignStore } from '../../store/designStore'
import * as THREE from 'three'

const BackgroundImagePlaneInner: React.FC = () => {
  const { canvasSettings } = useDesignStore()
  
  // Don't render if no background image is set
  if (!canvasSettings.backgroundImage) {
    return null
  }

  // Load texture
  const texture = useTexture(canvasSettings.backgroundImage, (texture) => {
    // Configure texture properties for better quality
    texture.wrapS = THREE.ClampToEdgeWrapping
    texture.wrapT = THREE.ClampToEdgeWrapping
    texture.minFilter = THREE.LinearFilter
    texture.magFilter = THREE.LinearFilter
    texture.flipY = false
  })

  // Calculate plane dimensions based on image aspect ratio
  const planeDimensions = useMemo(() => {
    if (!texture || !texture.image) {
      return [canvasSettings.width, canvasSettings.height]
    }

    const imageAspect = texture.image.width / texture.image.height
    const canvasAspect = canvasSettings.width / canvasSettings.height
    
    let width = canvasSettings.width
    let height = canvasSettings.height
    
    // Scale to fit the canvas while maintaining aspect ratio
    if (imageAspect > canvasAspect) {
      // Image is wider than canvas
      height = width / imageAspect
    } else {
      // Image is taller than canvas
      width = height * imageAspect
    }
    
    return [
      width * (canvasSettings.backgroundImageScale || 1.0),
      height * (canvasSettings.backgroundImageScale || 1.0)
    ]
  }, [texture, canvasSettings.width, canvasSettings.height, canvasSettings.backgroundImageScale])

  const imagePosition = useMemo(() => {
    return [
      canvasSettings.backgroundImageOffsetX || 0,
      -0.05 + (canvasSettings.backgroundImageOffsetY || 0),
      canvasSettings.backgroundImageOffsetZ || 0
    ] as [number, number, number]
  }, [canvasSettings.backgroundImageOffsetX, canvasSettings.backgroundImageOffsetY, canvasSettings.backgroundImageOffsetZ])

  const imageRotation = useMemo(() => {
    return [
      -Math.PI / 2 + (canvasSettings.backgroundImageRotationX || 0),
      canvasSettings.backgroundImageRotationY || 0,
      canvasSettings.backgroundImageRotationZ || 0
    ] as [number, number, number]
  }, [canvasSettings.backgroundImageRotationX, canvasSettings.backgroundImageRotationY, canvasSettings.backgroundImageRotationZ])

  return (
    <mesh
      position={imagePosition} // Use dynamic position for panning
      rotation={imageRotation} // Use dynamic rotation
      receiveShadow={false} // Background shouldn't receive shadows
    >
      <planeGeometry args={[planeDimensions[0], planeDimensions[1]]} />
      <meshBasicMaterial
        map={texture}
        transparent
        opacity={canvasSettings.backgroundImageOpacity || 0.7}
        side={THREE.DoubleSide}
        depthWrite={false} // Prevent z-fighting with ground plane
      />
    </mesh>
  )
}

// Main component with error boundary
const BackgroundImagePlane: React.FC = () => {
  const { canvasSettings } = useDesignStore()
  
  // Don't render anything if no background image
  if (!canvasSettings.backgroundImage) {
    return null
  }

  return (
    <Suspense fallback={null}>
      <BackgroundImagePlaneInner />
    </Suspense>
  )
}

export default BackgroundImagePlane
